/**
 * Media utilities for handling asset URLs
 *
 * SINGLE SOURCE OF TRUTH for the public host that serves our Cloudflare R2
 * bucket. No other client file may hardcode the bucket hostname — import from
 * here instead, so moving the bucket to a custom domain is one env var.
 *
 * The server (server/src/utils/mediaUrls.ts) and the marketing site
 * (website/src/lib/media.ts) have their own copies because they are deployed
 * separately and read different env vars. Change the fallback in one, change
 * it in all three.
 */

/**
 * Fallback host, used when REACT_APP_MEDIA_BASE_URL is unset. This is the ONLY
 * place the bucket hostname is written down in the client.
 *
 * NOTE: this env var is currently NOT set in production, so this fallback is
 * what production actually uses. Set it on the client service before changing
 * the bucket host, or the switch will not take effect in the app.
 */
const FALLBACK_MEDIA_BASE_URL = 'https://pub-122b4864d4c74adba4c23b8723c56973.r2.dev';

/**
 * Get the base URL for media assets from Cloudflare R2 (now public)
 */
export const getMediaBaseUrl = (): string => {
  const configured = process.env.REACT_APP_MEDIA_BASE_URL;
  const base = configured && configured.trim() ? configured.trim() : FALLBACK_MEDIA_BASE_URL;
  return base.replace(/\/+$/, '');
};

/**
 * Build an absolute URL for any object in the bucket.
 * @param objectPath - Bucket key, with or without a leading slash
 */
export const buildMediaUrl = (objectPath: string): string => {
  return `${getMediaBaseUrl()}/${objectPath.replace(/^\/+/, '')}`;
};

/**
 * Get the full URL for an icon asset from public Cloudflare R2
 * @param iconName - The icon filename (e.g., 'zoom.svg')
 * @returns Full URL to the icon
 */
export const getIconUrl = (iconName: string): string => {
  return buildMediaUrl(`system/icons/${iconName}`);
};

/**
 * Get a shared marketing/system illustration (the `images/system/` prefix).
 * @param fileName - e.g. 'Sailboatwithtree.png'
 */
export const getSystemImageUrl = (fileName: string): string => {
  return buildMediaUrl(`images/system/${fileName}`);
};

/**
 * Shared brand assets. These live under environment-agnostic prefixes, so they
 * are identical across local, staging and production.
 */
export const SAILLAB_LOGO_URL = buildMediaUrl('icon/SailLab_Logo_Horizontal.png');
export const SAILLAB_FAVICON_URL = buildMediaUrl('icon/sailLab_favicon_512x512.png');

/**
 * Video conferencing provider icons
 */
export const VIDEO_PROVIDER_ICONS = {
  zoom: 'zoom.svg',
  google_meet: 'googlemeet.svg',
  microsoft_teams: 'microsoftteams.svg'
} as const;

/**
 * Calendar provider icons
 */
export const CALENDAR_PROVIDER_ICONS = {
  google: 'googlecalendar.svg',
  microsoft: 'microsoftoutlook.svg'
} as const;

/**
 * Video provider display info including fallback
 */
export const VIDEO_PROVIDER_INFO = {
  zoom: { icon: 'zoom.svg', fallback: 'Z', color: 'bg-blue-500' },
  google_meet: { icon: 'googlemeet.svg', fallback: 'GM', color: 'bg-green-500' },
  microsoft_teams: { icon: 'microsoftteams.svg', fallback: 'MT', color: 'bg-purple-500' }
} as const;

/**
 * Calendar provider display info including fallback
 */
export const CALENDAR_PROVIDER_INFO = {
  google: { icon: 'googlecalendar.svg', fallback: 'G', color: 'bg-blue-500' },
  microsoft: { icon: 'microsoftoutlook.svg', fallback: 'M', color: 'bg-blue-600' }
} as const;

/**
 * Get video provider icon URL
 * @param provider - The video provider key
 * @returns Full URL to the provider's icon
 */
export const getVideoProviderIconUrl = (provider: keyof typeof VIDEO_PROVIDER_ICONS): string => {
  return getIconUrl(VIDEO_PROVIDER_ICONS[provider]);
};

/**
 * Get video provider fallback info
 * @param provider - The video provider key
 * @returns Fallback display info for the provider
 */
export const getVideoProviderFallback = (provider: keyof typeof VIDEO_PROVIDER_INFO) => {
  return VIDEO_PROVIDER_INFO[provider];
};

/**
 * Get calendar provider icon URL
 * @param provider - The calendar provider key
 * @returns Full URL to the provider's icon
 */
export const getCalendarProviderIconUrl = (provider: keyof typeof CALENDAR_PROVIDER_ICONS): string => {
  return getIconUrl(CALENDAR_PROVIDER_ICONS[provider]);
};

/**
 * Get calendar provider fallback info
 * @param provider - The calendar provider key
 * @returns Fallback display info for the provider
 */
export const getCalendarProviderFallback = (provider: keyof typeof CALENDAR_PROVIDER_INFO) => {
  return CALENDAR_PROVIDER_INFO[provider];
};